import { IncSelectOption } from "@inception/ui";
import { InsightDescription, UseCaseConfig } from "../services/api";
import { logger } from "../core";
import { CompareOperator, OpCreationConfig, OpInsightType } from "../services/api/operationalise";
import { isAppBicycleRelease, isPreviewRelease } from "../../appConfig";

export class CopilotUtils {
  static getKpiOptions(useCaseConfig: UseCaseConfig): IncSelectOption[] {
    const { dataPreferences } = useCaseConfig || {};
    const { topKPIs } = dataPreferences || {};
    return (topKPIs?.bizDataQuery || [])
      .map(bizDataQuery => {
        const { buildingBlockConfig, labels, widgetConfig } = bizDataQuery;
        const name = buildingBlockConfig?.name || widgetConfig?.name || labels.name || "";

        if (!name) {
          logger.warn("CopilotUtils", `Could not find name for KPI`, bizDataQuery);
        }

        return name
          ? {
              label: name,
              value: name
            }
          : null;
      })
      .filter(Boolean);
  }

  static getInsightDescriptionForOpCreationConfig(opCreationConfig: OpCreationConfig) {
    const { bizActions, description, name, opCreationConfigMetadata, idProps } = opCreationConfig;

    const { insightType, isOpportunity, spikeDropCompareOperator, causeMetaData, importance } =
      opCreationConfigMetadata || {};

    const automation = Object.values(bizActions || {})
      .map(act => act.alertActionConfig?.name)
      .filter(Boolean)
      .join(", ");
    const causes = causeMetaData?.causes || [];

    const insightDesc: InsightDescription = {
      automation,
      causes,
      description,
      name,
      icon: "diagram",
      importance: importance || "",
      insightType: insightType || OpInsightType.REAL_TIME_DETECTION,
      isOpportunity: isOpportunity || false,
      spikeDropCompareOperator: spikeDropCompareOperator || CompareOperator.Below,
      metric: "",
      trigger: "",
      causeMetaData,
      involvedDimensions: [],
      involvedEvents: (idProps?.primary?.eventTypes?.userServiceInfo || []).map(et => et.userServiceEntityId)
    };

    return insightDesc;
  }

  /**
   * Returns the appropriate term based on release type:
   * - "Use Case Agents" for preview or app-bicycle releases
   * - "Copilots" for other releases
   */
  static getUseCaseAgentsDisplayText(): string {
    return isPreviewRelease || isAppBicycleRelease ? "Use Case Agents" : "Copilots";
  }

  /**
   * Returns "Use Case Agent" (singular) or "Copilot" (singular) based on release type
   */
  static getUseCaseAgentDisplayText(): string {
    return isPreviewRelease || isAppBicycleRelease ? "Use Case Agent" : "Copilot";
  }

  /**
   * Returns "All Use Case Agents" or "All Copilots" based on release type
   */
  static getAllUseCaseAgentsDisplayText(): string {
    return isPreviewRelease || isAppBicycleRelease ? "All Use Case Agents" : "All Copilots";
  }

  /**
   * Returns "My Use Case Agents" or "My Copilots" based on release type
   */
  static getMyUseCaseAgentsDisplayText(): string {
    return isPreviewRelease || isAppBicycleRelease ? "My Use Case Agents" : "My Copilots";
  }
}
